<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';

const DAY_NAMES = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];
const appointments = ref([]);
const professionals = ref([]);
const establishments = ref([]);
const isLoading = ref(true);
const errorMessage = ref('');
const weekStart = ref(startOfWeek(new Date()));
const selectedProfessionalId = ref('');
const showProfessionals = ref(false);
const newProfessional = ref({ name: '', specialty: '', color: '#2563eb' });
const router = useRouter();
const { accountId } = useAccount();

const days = computed(() =>
  Array.from({ length: 7 }, (_, index) => addDays(weekStart.value, index))
);

const weekLabel = computed(() => {
  const first = days.value[0];
  const last = days.value[6];
  const options = { day: '2-digit', month: 'short' };
  return `${first.toLocaleDateString('pt-BR', options)} - ${last.toLocaleDateString('pt-BR', options)}`;
});

function isoDate(date) {
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60 * 1000)
    .toISOString()
    .slice(0, 10);
}

function startOfWeek(date) {
  const value = new Date(date);
  value.setHours(0, 0, 0, 0);
  value.setDate(value.getDate() - value.getDay());
  return value;
}

function addDays(date, amount) {
  const value = new Date(date);
  value.setDate(value.getDate() + amount);
  return value;
}

function dailyAppointments(day) {
  const date = isoDate(day);
  return appointments.value.filter(item => item.scheduled_date === date);
}

function professionalName(appointment) {
  return appointment.professionals?.name || 'Sem profissional';
}

function establishmentName(appointment) {
  return appointment.establishments?.name || 'Sem estabelecimento';
}

function commercialOfferLabel(appointment) {
  const offer = appointment.commercial_offers;
  if (!offer) return '';
  const price = new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: offer.currency || 'EUR',
  }).format(Number(offer.promotional_price_cents || 0) / 100);
  const campaignName = offer.commercial_campaigns?.name;
  return `${campaignName || offer.name} - ${price}`;
}

function appointmentProfessionals(appointment) {
  if (!appointment.establishment_id) return professionals.value;
  return professionals.value.filter(professional =>
    (professional.professional_establishments || []).some(
      link => link.is_active && link.establishment_id === appointment.establishment_id
    )
  );
}

function statusLabel(status) {
  const labels = {
    confirmed: 'Confirmado',
    cancelled: 'Cancelado',
    rescheduled: 'Reagendado',
    completed: 'Concluido',
    no_show: 'Nao compareceu',
  };
  return labels[status] || status;
}

function statusClass(status) {
  const classes = {
    confirmed: 'bg-n-teal-3 text-n-teal-11',
    cancelled: 'bg-n-ruby-3 text-n-ruby-11',
    rescheduled: 'bg-n-amber-3 text-n-amber-11',
    completed: 'bg-n-slate-3 text-n-slate-11',
    no_show: 'bg-n-ruby-3 text-n-ruby-11',
  };
  return classes[status] || 'bg-n-slate-3 text-n-slate-11';
}

function openConversation(appointment) {
  const conversationId =
    appointment.conversations?.external_conversation_id;
  if (!conversationId) return;
  router.push(`/app/accounts/${accountId.value}/conversations/${conversationId}`);
}

async function fetchAppointments() {
  isLoading.value = true;
  errorMessage.value = '';
  const from = isoDate(days.value[0]);
  const to = isoDate(days.value[6]);
  const params = new URLSearchParams({ from, to });
  if (selectedProfessionalId.value) {
    params.set('professionalId', selectedProfessionalId.value);
  }

  try {
    const response = await fetch(`/api/superwoot/appointments?${params}`);
    if (!response.ok) throw new Error('request_failed');
    const payload = await response.json();
    appointments.value = payload.appointments || [];
  } catch {
    errorMessage.value = 'Nao foi possivel carregar os agendamentos.';
  } finally {
    isLoading.value = false;
  }
}

async function fetchProfessionals() {
  try {
    const response = await fetch('/api/superwoot/professionals');
    if (!response.ok) throw new Error('request_failed');
    const payload = await response.json();
    professionals.value = payload.professionals || [];
  } catch {
    errorMessage.value = 'Nao foi possivel carregar os profissionais.';
  }
}

async function fetchEstablishments() {
  try {
    const response = await fetch('/api/superwoot/establishments');
    if (!response.ok) throw new Error('request_failed');
    const payload = await response.json();
    establishments.value = payload.establishments || [];
  } catch {
    errorMessage.value = 'Nao foi possivel carregar os estabelecimentos.';
  }
}

async function createProfessional() {
  if (!newProfessional.value.name.trim()) return;

  try {
    const response = await fetch('/api/superwoot/professionals', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newProfessional.value),
    });
    if (!response.ok) throw new Error('request_failed');
    newProfessional.value = { name: '', specialty: '', color: '#2563eb' };
    await fetchProfessionals();
  } catch {
    errorMessage.value = 'Nao foi possivel cadastrar o profissional.';
  }
}

async function assignProfessional(appointment, professionalId) {
  try {
    const response = await fetch('/api/superwoot/appointments', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        appointmentId: appointment.id,
        status: appointment.status,
        professionalId: professionalId || null,
      }),
    });
    if (!response.ok) {
      const payload = await response.json();
      throw new Error(payload.error || 'request_failed');
    }
    await fetchAppointments();
  } catch (error) {
    errorMessage.value =
      error.message === 'professional_slot_unavailable'
        ? 'Este profissional ja possui atendimento nesse horario.'
        : 'Nao foi possivel atribuir o profissional.';
  }
}

async function assignEstablishment(appointment, establishmentId) {
  try {
    const response = await fetch('/api/superwoot/appointments', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        appointmentId: appointment.id,
        status: appointment.status,
        establishmentId: establishmentId || null,
      }),
    });
    if (!response.ok) throw new Error('request_failed');
    await fetchAppointments();
  } catch {
    errorMessage.value = 'Nao foi possivel atribuir o estabelecimento.';
  }
}

async function updateAppointmentStatus(appointment, status) {
  try {
    const response = await fetch('/api/superwoot/appointments', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        appointmentId: appointment.id,
        status,
      }),
    });
    if (!response.ok) throw new Error('request_failed');
    await fetchAppointments();
  } catch {
    errorMessage.value = 'Nao foi possivel atualizar o status do atendimento.';
  }
}

function goToCurrentWeek() {
  weekStart.value = startOfWeek(new Date());
}

function changeWeek(amount) {
  weekStart.value = addDays(weekStart.value, amount);
}

watch([weekStart, selectedProfessionalId], fetchAppointments);
onMounted(async () => {
  await Promise.all([fetchProfessionals(), fetchEstablishments()]);
  await fetchAppointments();
});
</script>

<template>
  <section class="flex flex-col flex-1 min-w-0 h-full bg-n-background">
    <header
      class="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-n-weak bg-n-solid-2"
    >
      <div>
        <h1 class="text-xl font-medium text-n-slate-12">Agendamentos</h1>
        <p class="mt-1 text-sm text-n-slate-11">
          Agenda semanal da equipe
        </p>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <select
          v-model="selectedProfessionalId"
          class="h-8 px-2 text-sm border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12"
        >
          <option value="">Equipe completa</option>
          <option value="unassigned">Sem profissional</option>
          <option
            v-for="professional in professionals"
            :key="professional.id"
            :value="professional.id"
          >
            {{ professional.name }}
          </option>
        </select>
        <Button
          icon="i-lucide-users"
          color="slate"
          size="sm"
          title="Gerenciar profissionais"
          @click="showProfessionals = !showProfessionals"
        />
        <span class="hidden text-sm text-n-slate-11 sm:inline">
          {{ weekLabel }}
        </span>
        <Button
          label="Hoje"
          color="slate"
          size="sm"
          @click="goToCurrentWeek"
        />
        <Button
          icon="i-lucide-chevron-left"
          color="slate"
          size="sm"
          title="Semana anterior"
          @click="changeWeek(-7)"
        />
        <Button
          icon="i-lucide-chevron-right"
          color="slate"
          size="sm"
          title="Proxima semana"
          @click="changeWeek(7)"
        />
      </div>
    </header>

    <div
      v-if="showProfessionals"
      class="flex flex-wrap items-end gap-3 px-6 py-3 border-b border-n-weak bg-n-solid-1"
    >
      <label class="flex flex-col gap-1 text-xs text-n-slate-11">
        Nome
        <input
          v-model="newProfessional.name"
          class="h-8 px-2 text-sm border rounded-md border-n-weak bg-n-solid-2 text-n-slate-12"
          placeholder="Nome do profissional"
        />
      </label>
      <label class="flex flex-col gap-1 text-xs text-n-slate-11">
        Especialidade
        <input
          v-model="newProfessional.specialty"
          class="h-8 px-2 text-sm border rounded-md border-n-weak bg-n-solid-2 text-n-slate-12"
          placeholder="Barbeiro, aplicacao..."
        />
      </label>
      <label class="flex flex-col gap-1 text-xs text-n-slate-11">
        Cor
        <input
          v-model="newProfessional.color"
          type="color"
          class="w-12 h-8 border rounded-md border-n-weak bg-n-solid-2"
        />
      </label>
      <Button
        label="Adicionar"
        color="blue"
        size="sm"
        @click="createProfessional"
      />
      <span class="text-xs text-n-slate-10">
        {{ professionals.length }} profissionais ativos
      </span>
    </div>

    <div v-if="errorMessage" class="px-6 py-4 text-sm text-n-ruby-11">
      {{ errorMessage }}
    </div>

    <div class="flex-1 min-h-0 p-4 overflow-auto">
      <div
        class="grid min-w-[980px] h-full grid-cols-7 overflow-hidden border rounded-lg border-n-weak bg-n-solid-2"
      >
        <article
          v-for="day in days"
          :key="isoDate(day)"
          class="flex flex-col min-w-0 border-r border-n-weak last:border-r-0"
        >
          <header
            class="flex items-center justify-between px-3 py-3 border-b border-n-weak bg-n-solid-3"
          >
            <span class="text-xs text-n-slate-11">
              {{ DAY_NAMES[day.getDay()] }}
            </span>
            <strong class="text-sm font-medium text-n-slate-12">
              {{ day.getDate() }}
            </strong>
          </header>
          <div class="flex-1 p-2 overflow-y-auto">
            <p
              v-if="!isLoading && dailyAppointments(day).length === 0"
              class="px-1 py-2 text-xs text-n-slate-10"
            >
              Livre
            </p>
            <article
              v-for="appointment in dailyAppointments(day)"
              :key="appointment.id"
              class="w-full p-2 mb-2 text-left border rounded-md cursor-pointer border-n-weak bg-n-solid-3 hover:bg-n-alpha-2"
              @click="openConversation(appointment)"
            >
              <time class="block text-xs font-medium text-n-blue-text">
                {{ appointment.scheduled_time.slice(0, 5) }}
              </time>
              <strong
                class="block mt-1 text-sm font-medium truncate text-n-slate-12"
              >
                {{ appointment.contact_name }}
              </strong>
              <span class="block mt-1 text-xs truncate text-n-slate-11">
                {{ appointment.service_type }}
              </span>
              <span class="block mt-1 text-xs truncate text-n-slate-11">
                {{ professionalName(appointment) }}
              </span>
              <span class="block mt-1 text-xs truncate text-n-slate-11">
                {{ establishmentName(appointment) }}
              </span>
              <span
                v-if="commercialOfferLabel(appointment)"
                class="block px-1.5 py-1 mt-2 text-xs truncate rounded bg-n-teal-3 text-n-teal-11"
              >
                {{ commercialOfferLabel(appointment) }}
              </span>
              <select
                class="w-full h-7 px-1 mt-2 text-xs border rounded border-n-weak bg-n-solid-2 text-n-slate-12"
                :value="appointment.professional_id || ''"
                @click.stop
                @change.stop="assignProfessional(appointment, $event.target.value)"
              >
                <option value="">Sem profissional</option>
                <option
                  v-for="professional in appointmentProfessionals(appointment)"
                  :key="professional.id"
                  :value="professional.id"
                >
                  {{ professional.name }}
                </option>
              </select>
              <select
                class="w-full h-7 px-1 mt-2 text-xs border rounded border-n-weak bg-n-solid-2 text-n-slate-12"
                :value="appointment.establishment_id || ''"
                @click.stop
                @change.stop="assignEstablishment(appointment, $event.target.value)"
              >
                <option value="">Sem estabelecimento</option>
                <option
                  v-for="establishment in establishments"
                  :key="establishment.id"
                  :value="establishment.id"
                >
                  {{ establishment.name }}
                </option>
              </select>
              <span
                class="inline-flex px-1.5 py-0.5 mt-2 text-xs rounded"
                :class="statusClass(appointment.status)"
              >
                {{ statusLabel(appointment.status) }}
              </span>
              <div
                v-if="!['completed', 'cancelled', 'no_show'].includes(appointment.status)"
                class="flex gap-1 pt-2 mt-2 border-t border-n-weak"
              >
                <Button
                  icon="i-lucide-circle-check"
                  size="sm"
                  color="teal"
                  title="Concluir atendimento"
                  @click.stop="updateAppointmentStatus(appointment, 'completed')"
                />
                <Button
                  icon="i-lucide-user-x"
                  size="sm"
                  color="slate"
                  title="Marcar falta"
                  @click.stop="updateAppointmentStatus(appointment, 'no_show')"
                />
                <Button
                  icon="i-lucide-circle-x"
                  size="sm"
                  color="ruby"
                  title="Cancelar agendamento"
                  @click.stop="updateAppointmentStatus(appointment, 'cancelled')"
                />
              </div>
            </article>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>
